import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Grid,
  Tooltip,
  Fab,
  Drawer,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DocumentIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Storage as ChunkIcon,
  MenuBook as KnowledgeIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { ragService } from '../services/ragService';
import { ROUTES } from '../constants';
import ChunkTester from '../components/ChunkTester';
import RetrievalMethodExplanation from '../components/common/RetrievalMethodExplanation';

const KnowledgeBaseDetail = () => {
  const navigate = useNavigate();
  const { kbId } = useParams();

  // Estados principais
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para teste de chunks (drawer)
  const [testDrawerOpen, setTestDrawerOpen] = useState(false);

  // Estados para visualização de chunks
  const [chunksDialogOpen, setChunksDialogOpen] = useState(false);
  const [documentChunks, setDocumentChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);

  // Estados para ações nos documentos
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Estados para atualização automática
  const [refreshingDocuments, setRefreshingDocuments] = useState(new Set());
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(null);

  // Carregar dados (kb + docs + stats) com coalescing do serviço
  useEffect(() => {
    loadData();
  }, [kbId]);

  // Configurar atualização automática para documentos em processamento
  useEffect(() => {
    const hasProcessingDocs = documents.some(doc => 
      doc.status === 'processing' || doc.status === 'pending'
    );

    if (hasProcessingDocs && !autoRefreshInterval) {
      // Configurar refresh automático a cada 10 segundos
      const interval = setInterval(() => {
        console.log('🔄 Auto-refresh: verificando documentos em processamento...');
        loadData();
      }, 10000);
      
      setAutoRefreshInterval(interval);
    } else if (!hasProcessingDocs && autoRefreshInterval) {
      // Limpar interval se não há mais documentos processando
      clearInterval(autoRefreshInterval);
      setAutoRefreshInterval(null);
    }

    // Cleanup ao desmontar componente
    return () => {
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
      }
    };
  }, [documents, autoRefreshInterval]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar KB primeiro (pequena), depois docs+stats em paralelo para pintar header rápido
      const kbData = await ragService.getKnowledgeBase(kbId);
      setKnowledgeBase(kbData);

      const [documentsData, statsData] = await Promise.all([
        ragService.getDocuments(kbId),
        ragService.getKnowledgeBaseStats(kbId).catch(() => null),
      ]);

      setDocuments(documentsData.documents || []);
      setStats(statsData);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar base de conhecimento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDocument) return;
    
    try {
      setDeleting(true);
      await ragService.deleteDocument(selectedDocument.id);
      
      // Recarregar documentos
      await loadData();
      
      setSnackbar({
        open: true,
        message: 'Documento excluído com sucesso!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Erro ao deletar documento:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir documento: ' + err.message,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    }
  };

  const handleReprocessDocument = async (doc) => {
    try {
      await ragService.reprocessDocument(doc.id);
      setSnackbar({
        open: true,
        message: 'Reprocessamento iniciado!',
        severity: 'success'
      });
      
      // Recarregar documentos após alguns segundos
      setTimeout(loadData, 2000);
    } catch (err) {
      console.error('Erro ao reprocessar:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao reprocessar documento: ' + err.message,
        severity: 'error'
      });
    }
  };

  const handleRefreshDocument = async (docId) => {
    try {
      setRefreshingDocuments(prev => new Set(prev).add(docId));
      
      // Invalida cache local para este KB antes de refetch
      if (ragService._cache?.docsByKb) ragService._cache.docsByKb.delete(kbId);
      if (ragService._cache?.statsByKb) ragService._cache.statsByKb.delete(kbId);

      const [documentsData, statsData] = await Promise.all([
        ragService.getDocuments(kbId),
        ragService.getKnowledgeBaseStats(kbId).catch(() => null),
      ]);

      setDocuments(documentsData.documents || []);
      setStats(statsData);
      
      setSnackbar({
        open: true,
        message: 'Status atualizado com sucesso!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Erro ao atualizar documento:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar status: ' + err.message,
        severity: 'error'
      });
    } finally {
      setRefreshingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  const handleViewChunks = async (doc) => {
    if (doc.status !== 'processed') {
      setSnackbar({
        open: true,
        message: 'Documento ainda não foi processado',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoadingChunks(true);
      setSelectedDocument(doc);
      setChunksDialogOpen(true);
      
      const response = await ragService.getDocumentChunks(doc.id);
      setDocumentChunks(response.chunks || []);
    } catch (err) {
      console.error('Erro ao carregar chunks:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar chunks: ' + err.message,
        severity: 'error'
      });
      setChunksDialogOpen(false);
    } finally {
      setLoadingChunks(false);
    }
  };

  const getStatusChip = (status, isRefreshing = false) => {
    const statusConfig = {
      processed: { label: 'Processado', color: 'success', icon: null },
      processing: { label: 'Processando', color: 'warning', icon: '⏳' },
      error: { label: 'Erro', color: 'error', icon: '❌' },
      pending: { label: 'Pendente', color: 'default', icon: '⏸️' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    if (isRefreshing) {
      return (
        <Chip 
          label="Atualizando..." 
          color="info" 
          size="small"
          icon={<CircularProgress size={12} />}
        />
      );
    }
    
    return (
      <Chip 
        label={`${config.icon || ''} ${config.label}`.trim()} 
        color={config.color} 
        size="small" 
      />
    );
  };

  // Aplicar configurações testadas de volta à KB
  const handleApplyTestConfig = async (newConfig) => {
    try {
      const updateData = {
        ...knowledgeBase,
        retrieval_method: newConfig.retrieval_method,
        retrieval_config: {
          ...knowledgeBase.retrieval_config,
          ...newConfig.retrieval_config
        }
      };

      await ragService.updateKnowledgeBase(kbId, updateData);
      
      // Recarregar dados para mostrar as novas configurações
      await loadData();
      
      setSnackbar({
        open: true,
        message: 'Configurações aplicadas com sucesso!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Erro ao aplicar configurações:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao aplicar configurações: ' + err.message,
        severity: 'error'
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!knowledgeBase) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Base de conhecimento não encontrada.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 10 }}>
      {/* Header com informações básicas da KB */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ mb: 4 }}>
          {/* Navegação de volta */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.KNOWLEDGE_BASE)}
            sx={{ mb: 3 }}
          >
            Voltar para Bases de Conhecimento
          </Button>

          {/* Cabeçalho principal */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                  {/* Ícone da KB */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha('#4caf50', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <KnowledgeIcon sx={{ color: 'success.main', fontSize: 28 }} />
                  </Box>

                  {/* Informações principais */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                      {knowledgeBase.name}
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {knowledgeBase.description || 'Base de conhecimento sem descrição'}
                    </Typography>

                    {/* Chips de status e configuração */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip 
                        label={knowledgeBase.is_active ? 'Ativa' : 'Inativa'} 
                        color={knowledgeBase.is_active ? 'success' : 'error'} 
                        size="small" 
                      />
                      <Chip 
                        label={`Método: ${knowledgeBase.retrieval_method === 'hybrid' ? 'Híbrido' : 'Semântico'}`} 
                        variant="outlined" 
                        size="small" 
                        color={knowledgeBase.retrieval_method === 'hybrid' ? 'success' : 'primary'}
                      />
                      <RetrievalMethodExplanation tooltipOnly />
                      <Chip 
                        label={`Modelo: ${knowledgeBase.embedding_model === 'openai_small' ? 'OpenAI Small' : knowledgeBase.embedding_model}`} 
                        variant="outlined" 
                        size="small" 
                      />
                      {knowledgeBase.retrieval_config && (
                        <>
                          <Chip 
                            label={`Top-k: ${knowledgeBase.retrieval_config.top_k || 5}`} 
                            variant="outlined" 
                            size="small" 
                          />
                          <Chip 
                            label={`Threshold: ${knowledgeBase.retrieval_config.threshold || 0.5}`} 
                            variant="outlined" 
                            size="small" 
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Ações principais */}
                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                  <Tooltip title="Editar base de conhecimento">
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(ROUTES.KNOWLEDGE_BASE_EDIT?.replace(':kbId', kbId) || `/knowledge-bases/${kbId}/edit`)}
                    >
                      Editar
                    </Button>
                  </Tooltip>
                  <Tooltip title="Upload de novo documento">
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => navigate(`/knowledge-base/${kbId}/upload`)}
                      sx={{ bgcolor: 'primary.main' }}
                    >
                      Upload
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {/* Contexto da KB se existir */}
              {knowledgeBase.context && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon sx={{ fontSize: 16 }} />
                      Contexto para Agentes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {knowledgeBase.context}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* Estatísticas compactas */}
          {stats && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={3} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Documentos */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocumentIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {stats.total_documents || documents.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Docs
                    </Typography>
                  </Box>

                  {/* Chunks */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChunkIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {documents.reduce((sum, doc) => sum + (doc.total_chunks || 0), 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Chunks
                    </Typography>
                  </Box>

                  {/* Processados */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {documents.filter(d => d.status === 'processed').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Processados
                    </Typography>
                  </Box>

                  {/* Agentes Usando */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <KnowledgeIcon sx={{ color: 'info.main', fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                      {stats.agents_using || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Agentes
                    </Typography>
                  </Box>

                  {/* Status de Processamento */}
                  {(() => {
                    const processingCount = documents.filter(d => d.status === 'processing' || d.status === 'pending').length;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {processingCount > 0 ? (
                          <CircularProgress size={16} color="warning" />
                        ) : (
                          <InfoIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                        )}
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: processingCount > 0 ? 'warning.main' : 'text.secondary'
                        }}>
                          {processingCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {processingCount > 0 ? 'Processando' : 'Em Fila'}
                        </Typography>
                        {processingCount > 0 && (
                          <Tooltip title="Atualizar">
                            <IconButton
                              size="small"
                              onClick={() => loadData()}
                              sx={{ p: 0.5, ml: 0.5 }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })()}
                </Stack>
              </CardContent>
            </Card>
          )}


        </Box>
      </motion.div>

      {/* Lista de Documentos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DocumentIcon />
                Documentos ({documents.length})
              </Typography>
              
              {documents.length === 0 && (
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => navigate(`/knowledge-base/${kbId}/upload`)}
                >
                  Primeiro Upload
                </Button>
              )}
            </Box>

            {documents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <FileIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum documento encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Faça upload do primeiro documento para começar a construir sua base de conhecimento.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => navigate(`/knowledge-base/${kbId}/upload`)}
                >
                  Upload do Primeiro Documento
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome do Arquivo</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Chunks</TableCell>
                      <TableCell>Configuração</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell width={100}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {doc.original_filename}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {doc.content_type}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusChip(doc.status, refreshingDocuments.has(doc.id))}
                            {(doc.status === 'processing' || doc.status === 'pending') && (
                              <Tooltip title="Atualizar status do documento">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRefreshDocument(doc.id)}
                                  disabled={refreshingDocuments.has(doc.id)}
                                  sx={{ 
                                    ml: 0.5,
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {doc.total_chunks || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {doc.chunk_size || 1000}ch / {doc.chunk_overlap || 200}ov
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(doc.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver chunks e ações">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setMenuAnchor(e.currentTarget);
                                setSelectedDocument(doc);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Botão Flutuante para Teste de Chunks */}
      <Tooltip title="Testar busca e chunks" placement="left">
        <Fab
          onClick={() => setTestDrawerOpen(true)}
          variant="extended"
          sx={{
            position: 'fixed',
            right: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: '#9c27b0', // Roxo igual ao botão fechar
            color: 'white',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            '&:hover': {
              bgcolor: '#7b1fa2',
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            },
            zIndex: 1200,
            transition: 'all 0.3s ease',
          }}
        >
          <SearchIcon sx={{ mr: 1 }} />
          Testar
        </Fab>
      </Tooltip>

      {/* Drawer de Teste de Chunks */}
      <Drawer
        anchor="right"
        open={testDrawerOpen}
        onClose={() => setTestDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '500px', md: '600px', lg: '650px' },
            maxWidth: '90vw',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        ModalProps={{
          keepMounted: true, // Melhor performance em mobile
        }}
        transitionDuration={300}
      >
        {/* Header do Drawer */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="info" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Teste de Busca e Chunks
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {knowledgeBase?.name || 'Base de conhecimento'}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setTestDrawerOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Subtítulo */}
        <Box sx={{ 
          px: 2, 
          py: 1, 
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography variant="body2" color="text.secondary">
            🔍 Teste como os chunks são recuperados e seus scores de relevância
          </Typography>
        </Box>

        {/* Conteúdo do Teste */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0,
        }}>
          <ChunkTester
            kbId={kbId}
            knowledgeBase={knowledgeBase}
            disabled={!knowledgeBase?.is_active}
            onClose={() => setTestDrawerOpen(false)}
            onApplyConfig={handleApplyTestConfig}
          />
        </Box>
      </Drawer>

      {/* Menu de ações do documento */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem 
          onClick={() => {
            handleViewChunks(selectedDocument);
            setMenuAnchor(null);
          }}
          disabled={selectedDocument?.status !== 'processed'}
        >
          <PreviewIcon sx={{ mr: 1 }} />
          Ver Chunks
        </MenuItem>
        <MenuItem onClick={() => {
          handleReprocessDocument(selectedDocument);
          setMenuAnchor(null);
        }}>
          <RefreshIcon sx={{ mr: 1 }} />
          Reprocessar
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            setDeleteDialogOpen(true);
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Deletar
        </MenuItem>
      </Menu>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o documento "<strong>{selectedDocument?.original_filename}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita e removerá todos os chunks deste documento.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteDocument}
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização de Chunks */}
      <Dialog 
        open={chunksDialogOpen} 
        onClose={() => setChunksDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChunkIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Chunks do Documento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedDocument?.original_filename}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {loadingChunks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : documentChunks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
              <ChunkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum chunk encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este documento ainda não foi processado ou não possui chunks.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                📊 {documentChunks.length} chunks encontrados
              </Typography>
              
              <Stack spacing={2}>
                {documentChunks.map((chunk, index) => (
                  <Card 
                    key={chunk.id || index} 
                    variant="outlined" 
                    sx={{ 
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <CardContent>
                      {/* Header do chunk */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Chunk #{chunk.chunk_index || index}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={`${chunk.content?.length || 0} chars`}
                            size="small"
                            variant="outlined"
                          />
                          {chunk.token_count && (
                            <Chip
                              label={`${chunk.token_count} tokens`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      </Box>
                      
                      {/* Conteúdo do chunk */}
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {chunk.content}
                        </Typography>
                      </Paper>
                      
                      {/* Metadados */}
                      {chunk.metadata && Object.keys(chunk.metadata).length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Metadados:
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {Object.entries(chunk.metadata).map(([key, value]) => (
                              <Typography key={key} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setChunksDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeBaseDetail; 